import React, { useState, useEffect } from "react"
import axios from "axios"
import { useNavigate, useParams } from "react-router-dom"
import { FaBars } from 'react-icons/fa'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import { FaEdit } from 'react-icons/fa'
import { apiURL, sitePath } from 'config/globals'
import Topbar from 'components/Topbar'
import Sidebar from 'components/Sidebar'
import Breadcrumbs from 'components/Breadcrumbs'
import FormRow from "components/FormRow"
import CancelButton from 'components/CancelButton'
import SaveButton from 'components/SaveButton'
import Footer from 'components/Footer'
import { checkAuth, authForm, isSuperAdmin } from 'utils/Auth'

const UserEdit = () => {
    const [currentUser, setCurrentUser] = useState(false)
    const [dbRoles, setDbRoles] = useState([])
    const [dbUser, setDbUser] = useState({})
    const [form, setForm] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const navigate = useNavigate()
    const { id } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            await getDbRoles()
            await getDbUser(id)
        }
        start()
    }, [])

    const getDbRoles = async () => {
        const response = await axios.get(`${apiURL}/get-db-roles/`)
        
        if (!response.data.success) {
            setErrorMessage(response.data.error || response.data)
            return
        }
        
        setDbRoles(response.data.rows)
    }

    const getDbUser = async (id) => {
        const response = await axios.get(`${apiURL}/get-db-user/${id}`)
        
        if (!response.data.success) {
            setErrorMessage(response.data.error || response.data)
            return
        }

        setDbUser(response.data.item)
    }

    const handleChange = (field, value) => {
        setForm({
            ...form,
            [field]: value
        })
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        event.stopPropagation()

        setIsSubmitting(true)

        try {
            const requestUrl = `${apiURL}/update-user/${dbUser.id}`
            const response = await axios.put(requestUrl, authForm(form))
            
            if (!response.data.success) {
                setErrorMessage(response.data.error || response.data)
                setIsSubmitting(false)
                return
            }

            navigate(`/users`)
        } catch (error) {
            setErrorMessage(JSON.stringify(error))
            setIsSubmitting(false)
        }
    }

    const cancel = () => {
        navigate(`/users`)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <Sidebar selectedItem="users" currentUser={currentUser} />
            <main>
                <Breadcrumbs items={[['Users', '/users'], [dbUser.name, `/users/${dbUser.id}`], ['Edit']]} />
                <div className="btn-toggle">
                    <FaBars />
                </div>
                <header>
                    <div className="float-start">
                        <h1>{dbUser.name}</h1>
                    </div>
                    {dbUser.id && (dbUser.id === currentUser.id || isSuperAdmin(currentUser)) &&
                        <Button variant="outline-primary" className="float-end px-4" href={`${sitePath}/users/${dbUser.id}/password`}>
                            <FaEdit /> Change Password
                        </Button>
                    }
                </header>
                {currentUser.id && dbUser.id &&
                    <>
                        <FormRow
                            id="userRole"
                            label="Role"
                            required={true}
                            content={
                                <Form.Select
                                    name="role"
                                    defaultValue={dbUser.role}
                                    disabled={dbUser.role === "1" || (dbUser.role === "2" && currentUser.role === "2" && dbUser.id !== currentUser.id)}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                >
                                    {dbRoles
                                        .filter(option => dbUser.role === "1" || option.id > 1)
                                        .map(option => (
                                            <option key={option.id} value={option.id}>
                                                {option.name}
                                            </option>
                                        ))
                                        .sort()
                                        .reverse()
                                    }
                                </Form.Select>
                            }
                        />
                        <FormRow
                            id="newUserName"
                            label="Name"
                            required={true}
                            content={
                                <Form.Control
                                    name="name"
                                    autoComplete="off"
                                    defaultValue={dbUser.name}
                                    disabled={dbUser.role === "1" || (dbUser.role === "2" && currentUser.role === "2" && dbUser.id !== currentUser.id)}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            id="newUserEmail"
                            label="Email"
                            required={true}
                            content={
                                <Form.Control
                                    name="email"
                                    autoComplete="off"
                                    defaultValue={dbUser.email}
                                    disabled={dbUser.role === "1" || (dbUser.role === "2" && currentUser.role === "2" && dbUser.id !== currentUser.id)}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        {errorMessage && 
                            <Alert key='submitError' variant='danger' className="mt-4">
                                {errorMessage}
                            </Alert>
                        }
                        <div className="mt-5 mb-4 d-grid d-lg-block">
                            {((dbUser.role !== "1" && (currentUser.role !== "2" || (dbUser.role === "2" && dbUser.id === currentUser.id))) || parseInt(dbUser.role) > 2) &&
                                <SaveButton isSubmitting={isSubmitting} onClick={handleSubmit} title="Save" />
                            }
                        </div>
                    </>
                }
                {errorMessage && 
                    <Alert key='errorMessage' variant='danger'>
                        {errorMessage}
                    </Alert>
                }
                <Footer user={currentUser} />
            </main>
        </>
    )
}

export default UserEdit